/**
 * Service Selector for Shopify Product Pages
 * Shows service options as a grid - selected options are added to cart with main product.
 * Supports multiple selections - state tracked internally, no visible checkbox.
 * Entire box is clickable.
 *
 * FIX: Only intercepts form submit when services are selected.
 * If no services are selected, the native theme product-form.js handles
 * the submit normally — preventing duplicate cart adds.
 */

class ServiceSelector {
  constructor() {
    this.serviceOptionsContainer = document.getElementById('service-options-container');
    this.serviceOptionsGrid = document.getElementById('service-options-grid');
    this.productForm = document.querySelector('.js-product-form-main');

    if (!this.serviceOptionsContainer || !this.serviceOptionsGrid) {
      console.warn('Service selector elements not found');
      return;
    }

    // Read handles from the wrapper div's data attribute
    const selectorWrapper = document.querySelector('.service-selector');
    if (!selectorWrapper) {
      console.warn('Service selector wrapper not found');
      return;
    }

    this.serviceHandles = (selectorWrapper.dataset.serviceHandles || '')
      .split(',')
      .map(h => h.trim())
      .filter(Boolean);

    this.serviceProducts = [];
    this.selectedServiceVariantIds = new Set();

    this.init();
  }

  async init() {
    await this.fetchServiceProducts();
    this.attachEventListeners();
  }

  async fetchServiceProducts() {
    try {
      const fetchPromises = this.serviceHandles.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(response => {
            if (!response.ok) throw new Error(`Failed to fetch product: ${handle}`);
            return response.json();
          })
      );

      this.serviceProducts = await Promise.all(fetchPromises);
      this.populateGrid();
    } catch (error) {
      console.error('Error fetching service products:', error);
      this.showError('Unable to load service options. Please refresh the page.');
    }
  }

  populateGrid() {
    this.serviceOptionsGrid.innerHTML = '';

    this.serviceProducts.forEach((product) => {
      if (!product.available) return; // Skip out of stock products

      const variant = product.variants[0]; // Use first variant
      const variantId = parseInt(variant.id);

      const box = document.createElement('div');
      box.className = 'service-option-box';
      box.dataset.variantId = variantId;
      box.dataset.productId = product.id;

      const title = document.createElement('div');
title.className = 'service-option-title';
title.textContent = product.title;

// Add ℹ️ icon to the 2nd product box only
if (this.serviceProducts.indexOf(product) === 1) {
  const infoLink = document.createElement('a');
  infoLink.href = 'https://www.mattresses4you.co.uk/pages/delivery-to-room-of-choice'; // 👈 change this to your page URL
  infoLink.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle; margin-left:3px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8"/><line x1="12" y1="12" x2="12" y2="16"/></svg>`;
infoLink.style.cssText = 'text-decoration:none; color:#333; vertical-align:middle;';
  infoLink.addEventListener('click', (e) => e.stopPropagation());
  title.appendChild(infoLink);
}

      const price = document.createElement('div');
      price.className = 'service-option-price';
      price.textContent = this.formatMoney(variant.price);

      box.appendChild(title);
      box.appendChild(price);

      // Click anywhere on the box toggles selection
      box.addEventListener('click', () => {
        if (this.selectedServiceVariantIds.has(variantId)) {
          // Deselect
          this.selectedServiceVariantIds.delete(variantId);
          box.classList.remove('selected');
        } else {
          // Select
          this.selectedServiceVariantIds.add(variantId);
          box.classList.add('selected');
        }
      });

      this.serviceOptionsGrid.appendChild(box);
    });
  }

  formatMoney(cents) {
    const pounds = (cents / 100).toFixed(2);
    return `£${pounds}`;
  }

  attachEventListeners() {
    if (this.productForm) {
      this.productForm.addEventListener('submit', (e) => {
        // KEY FIX: Only intercept the submit if services are selected.
        // If nothing is selected, let the theme's native product-form.js
        // handle the submission as normal — no duplicate cart adds.
        if (this.selectedServiceVariantIds.size > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleAddToCart(e);
        }
        // If size === 0, do nothing — theme handles it normally.
      });
    }
  }

  async handleAddToCart(event) {
    const formData = new FormData(this.productForm);
    const mainVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);

    // Build items array - main product always included
    const items = [{
      id: parseInt(mainVariantId),
      quantity: quantity
    }];

    // Add all selected service products
    this.selectedServiceVariantIds.forEach(variantId => {
      items.push({
        id: variantId,
        quantity: quantity
      });
    });

    // Collect line item properties from the form
    const properties = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    if (Object.keys(properties).length > 0) {
      items[0].properties = properties;
    }

    try {
      this.setLoadingState(true);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Successfully added to cart:', result);

        // Fire cart refresh events for theme compatibility
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        window.dispatchEvent(new CustomEvent('cartUpdated'));

        window.location.href = '/cart';
      } else {
        const error = await response.json();
        console.error('Add to cart error:', error);
        this.showError(error.description || 'Error adding to cart. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      this.showError('Network error. Please check your connection and try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    const submitButton = this.productForm?.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = isLoading;
      if (isLoading) {
        submitButton.dataset.originalText = submitButton.textContent;
        submitButton.textContent = 'Adding...';
      } else if (submitButton.dataset.originalText) {
        submitButton.textContent = submitButton.dataset.originalText;
      }
    }
  }

  showError(message) {
    const errorContainer = this.productForm?.querySelector('.js-form-error');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.hidden = false;
      setTimeout(() => { errorContainer.hidden = true; }, 5000);
    } else {
      alert(message);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ServiceSelector());
} else {
  new ServiceSelector();
}